// Updated: Reminder hover highlights recipients in orange, smaller event titles, removed tikkie label, added "Kopieer link" text, fixed table alignment, fixed vertical centering of grid rows
import React, { useEffect, useState } from 'react';
import emailjs from '@emailjs/browser';
import { supabase } from '../../../lib/supabase';
import { useToastContext } from '../../../contexts/ToastContext';
import { CalendarCheck, ChevronDown, ChevronRight, Users, Mail, Phone, Link, Check, CircleOff, Send, Loader2 } from 'lucide-react';

const EMAILJS_SERVICE_ID = 'service_ab74jwr';
const EMAILJS_PUBLIC_KEY = 'cO9IDwMo-d21soBmv';
const TEMPLATE_CONFIRMATION = 'template_eanob52';

interface Registration {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  registered_at: string;
}

interface PaymentClick {
  event_id: string;
  email: string;
  clicked_at: string;
}

interface JoinableEvent {
  id: string;
  date: string;
  description: string;
  tikkie_url?: string | null;
  tikkie_price_label?: string | null;
  registrations: Registration[];
  registration_count: number;
}

const EventRegistrationsSection = () => {
  const [upcomingEvents, setUpcomingEvents] = useState<JoinableEvent[]>([]);
  const [pastEvents, setPastEvents] = useState<JoinableEvent[]>([]);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [paymentClicks, setPaymentClicks] = useState<PaymentClick[]>([]);
  const [resendingEventId, setResendingEventId] = useState<string | null>(null);
  const [hoveredReminderId, setHoveredReminderId] = useState<string | null>(null);
  const { addToast } = useToastContext();

  const copyEventLink = async (eventId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/?event=${eventId}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(eventId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      console.error('Failed to copy link');
    }
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(Date.UTC(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day)
    ));
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  const formatRegistrationDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data: events, error: eventsError } = await supabase
        .from('events')
        .select('id, date, description, tikkie_url, tikkie_price_label')
        .eq('joinable', true)
        .order('date', { ascending: true });

      if (eventsError) throw eventsError;
      if (!events || events.length === 0) {
        setUpcomingEvents([]);
        setPastEvents([]);
        setLoading(false);
        return;
      }

      const { data: registrations, error: regError } = await supabase
        .from('event_registrations')
        .select('*')
        .in('event_id', events.map(e => e.id))
        .order('registered_at', { ascending: true });

      if (regError) throw regError;

      const { data: clicks, error: clicksError } = await supabase
        .from('payment_clicks')
        .select('event_id, email, clicked_at')
        .in('event_id', events.map(e => e.id))
        .order('clicked_at', { ascending: false });

      if (!clicksError && clicks) {
        setPaymentClicks(clicks);
      }

      const today = new Date().toISOString().split('T')[0];
      const upcoming: JoinableEvent[] = [];
      const past: JoinableEvent[] = [];

      for (const event of events) {
        const eventRegs = (registrations || []).filter(r => r.event_id === event.id);
        const joinableEvent: JoinableEvent = {
          ...event,
          registrations: eventRegs,
          registration_count: eventRegs.length
        };

        if (event.date >= today) {
          upcoming.push(joinableEvent);
        } else {
          past.push(joinableEvent);
        }
      }

      past.sort((a, b) => b.date.localeCompare(a.date));

      setUpcomingEvents(upcoming);
      setPastEvents(past);
    } catch (error) {
      console.error('Error fetching event registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (eventId: string) => {
    setExpandedEvent(prev => prev === eventId ? null : eventId);
  };

  const getPaymentClick = (eventId: string, email: string) =>
    paymentClicks.find(c => c.event_id === eventId && c.email === email.toLowerCase());

  const formatClickDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const handleResendConfirmations = async (event: JoinableEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    const notClicked = event.registrations.filter(
      reg => !getPaymentClick(event.id, reg.email)
    );

    if (notClicked.length === 0) {
      addToast('Alle aangemelden hebben al op de betaalknop geklikt', 'success');
      return;
    }

    setResendingEventId(event.id);
    let sent = 0;
    let failed = 0;

    for (const reg of notClicked) {
      const paymentUrl = `https://www.sisterhoodmemberclub.nl/pay?event=${event.id}&email=${encodeURIComponent(reg.email.toLowerCase())}`;
      const mailContent = `<p>Hey ${reg.first_name},</p>
<p>Wat leuk dat je je hebt aangemeld voor de Sisterhood lunch (ook toegankelijk voor niet-members) op 8 mei 2026.</p>
<p>Je aanmelding is bijna definitief. Om je plek officieel te bevestigen, hoef je alleen nog de betaling af te ronden via onderstaande button:</p>
<p style="margin: 24px 0;">
  <a href="${paymentUrl}" style="display:inline-block;padding:10px 20px;background-color:#E37C64;color:#ffffff;text-decoration:none;border-radius:9999px;font-size:14px;font-weight:500;font-family:sans-serif;">Bevestig je aanmelding</a>
</p>
<p>Zodra de betaling is voltooid, is jouw aanmelding definitief.</p>
<p>We kijken ernaar uit je te verwelkomen!</p>
<p>Hartelijke groet,<br>Kitty en Renee<br>Sisterhood Memberclub</p>`;

      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          TEMPLATE_CONFIRMATION,
          { mailContent, customerMail: reg.email.toLowerCase() },
          EMAILJS_PUBLIC_KEY
        );
        sent++;
      } catch (err) {
        console.error(`Failed to send to ${reg.email}:`, err);
        failed++;
      }
    }

    setResendingEventId(null);

    if (failed === 0) {
      addToast(`Bevestigingsmail verstuurd naar ${sent} persoon/personen`, 'success');
    } else {
      addToast(`${sent} verstuurd, ${failed} mislukt`, 'error');
    }
  };

  const getNotClickedCount = (event: JoinableEvent) =>
    event.registrations.filter(reg => !getPaymentClick(event.id, reg.email)).length;

  const renderEventRow = (event: JoinableEvent, isPast: boolean) => {
    const notClickedCount = getNotClickedCount(event);
    const isResending = resendingEventId === event.id;

    return (
      <div key={event.id} className="border rounded-lg overflow-hidden">
        <button
          onClick={() => toggleExpand(event.id)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            {expandedEvent === event.id
              ? <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
              : <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
            }
            <div className="min-w-0 text-left">
              <p className={`text-sm font-medium ${isPast ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                {event.description}
              </p>
              <p className={`text-xs ${isPast ? 'text-gray-400' : 'text-gray-500'}`}>
                {formatDate(event.date)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            {notClickedCount > 0 && !isPast && (
              <button
                onClick={(e) => handleResendConfirmations(event, e)}
                onMouseEnter={() => setHoveredReminderId(event.id)}
                onMouseLeave={() => setHoveredReminderId(null)}
                disabled={isResending}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-md transition-colors disabled:opacity-60"
                title={`Stuur bevestigingsmail naar ${notClickedCount} persoon/personen die nog niet hebben betaald`}
              >
                {isResending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                {isResending ? 'Versturen...' : `Herinnering (${notClickedCount})`}
              </button>
            )}
            {!isPast && (
              <button
                onClick={(e) => copyEventLink(event.id, e)}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                title="Kopieer aanmeldlink"
              >
                {copiedId === event.id ? (
                  <Check className="w-3.5 h-3.5 text-green-600" />
                ) : (
                  <Link className="w-3.5 h-3.5" />
                )}
                <span>{copiedId === event.id ? 'Gekopieerd!' : 'Kopieer link'}</span>
              </button>
            )}
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
              isPast ? 'bg-gray-100 text-gray-500' : 'bg-[#5A130E]/10 text-[#5A130E]'
            }`}>
              <Users className="w-3 h-3" />
              {event.registration_count}
            </span>
          </div>
        </button>

        {expandedEvent === event.id && (
          <div className="border-t bg-gray-50 px-4 py-3">
            {event.registrations.length === 0 ? (
              <p className="text-sm text-gray-500 italic">Geen aanmeldingen</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_1fr_1.5fr_1fr_80px_1fr_1fr] gap-3 text-xs font-medium text-gray-500 uppercase tracking-wider pb-2 border-b border-gray-200 px-1 items-center">
                  <span>Voornaam</span>
                  <span>Achternaam</span>
                  <span>E-mail</span>
                  <span>Telefoon</span>
                  <span>Link geklikt</span>
                  <span>Geklikt op</span>
                  <span>Aangemeld op</span>
                </div>
                {event.registrations.map((reg) => {
                  const click = getPaymentClick(event.id, reg.email);
                  const isReminderTarget = hoveredReminderId === event.id && !click;
                  return (
                    <div
                      key={reg.id}
                      className={`grid grid-cols-[1fr_1fr_1.5fr_1fr_80px_1fr_1fr] gap-3 text-sm py-2 rounded-md px-1 transition-colors items-center ${isReminderTarget ? 'bg-orange-100/70' : click ? 'bg-green-50' : ''}`}
                    >
                      <span className="text-gray-900">{reg.first_name}</span>
                      <span className="text-gray-900">{reg.last_name}</span>
                      <a href={`mailto:${reg.email}`} className="text-[#5A130E] hover:underline flex items-center gap-1 truncate">
                        <Mail className="w-3 h-3 shrink-0" />
                        <span className="truncate">{reg.email}</span>
                      </a>
                      {reg.phone ? (
                        <a href={`tel:${reg.phone}`} className="text-gray-700 hover:underline flex items-center gap-1">
                          <Phone className="w-3 h-3 shrink-0" />
                          {reg.phone}
                        </a>
                      ) : (
                        <span className="text-gray-300 text-xs italic">-</span>
                      )}
                      <span className="flex items-center justify-center" title={click ? `Betaallink geklikt op ${formatClickDate(click.clicked_at)}` : 'Nog niet geklikt'}>
                        {click ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <CircleOff className="w-4 h-4 text-gray-300" />
                        )}
                      </span>
                      <span className={`text-xs ${click ? 'text-green-700 font-medium' : 'text-gray-300 italic'}`}>
                        {click ? formatClickDate(click.clicked_at) : '—'}
                      </span>
                      <span className="text-gray-400 text-xs">{formatRegistrationDate(reg.registered_at)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="text-gray-500">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <CalendarCheck className="w-5 h-5 text-[#5A130E]" />
          <h2 className="text-xl font-semibold text-gray-900">Event aanmeldingen</h2>
        </div>

        {upcomingEvents.length === 0 && pastEvents.length === 0 ? (
          <p className="text-gray-500">Geen aanmeldbare evenementen gevonden.</p>
        ) : (
          <div className="space-y-8">
            {upcomingEvents.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                  Aankomende events
                </h3>
                <div className="space-y-3">
                  {upcomingEvents.map(event => renderEventRow(event, false))}
                </div>
              </div>
            )}

            {pastEvents.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
                  Verlopen events
                </h3>
                <div className="space-y-3">
                  {pastEvents.map(event => renderEventRow(event, true))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventRegistrationsSection;
